import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { X, Loader2, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useAuthor } from '@/hooks/useAuthor';
import { useFollow } from '@/hooks/useFollow';
import { Skeleton } from '@/components/ui/skeleton';

interface UserListSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;              // "Following" or "Followers"
  pubkeys: string[];          // full list of pubkeys
  isLoading: boolean;         // true while initial fetch is in flight
}

const PAGE_SIZE = 30;

/**
 * A single user row inside the list
 */
function UserRow({ pubkey }: { pubkey: string }) {
  const navigate = useNavigate();
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const npub = nip19.npubEncode(pubkey);
  const { isFollowing, toggleFollow, isToggling } = useFollow(pubkey);

  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/20 transition-colors cursor-pointer border-b border-border/30"
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/${npub}`);
      }}
    >
      {/* Avatar */}
      <img
        src={metadata?.picture || `https://robohash.org/${pubkey}.png?set=set4`}
        alt={metadata?.name || 'User'}
        className="size-10 rounded-full object-cover bg-secondary border border-border shrink-0"
        onError={(e) => { (e.target as HTMLImageElement).src = `https://robohash.org/${pubkey}.png?set=set4`; }}
      />

      {/* Name and handle */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <span className="text-sm font-bold truncate">
            {metadata?.display_name || metadata?.name || 'Anonymous'}
          </span>
        </div>
        <span className="text-xs text-muted-foreground truncate block">
          @{npub.slice(0, 8)}...{npub.slice(-4)}
        </span>
      </div>

      {/* Follow / Following button */}
      {!isToggling ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleFollow();
          }}
          className={`shrink-0 rounded-full text-xs font-bold px-4 py-1.5 transition-colors cursor-pointer ${
            isFollowing
              ? 'bg-secondary text-foreground border border-border hover:bg-rose-500/10 hover:text-rose-500 hover:border-rose-500/30'
              : 'bg-foreground text-background hover:bg-foreground/90'
          }`}
        >
          {isFollowing ? 'Following' : 'Follow'}
        </button>
      ) : (
        <div className="shrink-0 px-4 py-1.5">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
}

/**
 * Skeleton placeholder for loading state
 */
function UserRowSkeleton() {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 animate-pulse border-b border-border/30">
      <Skeleton className="size-10 rounded-full shrink-0" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-4 w-24 rounded-md" />
        <Skeleton className="h-3 w-16 rounded-md" />
      </div>
      <Skeleton className="h-7 w-16 rounded-full shrink-0" />
    </div>
  );
}

/**
 * UserListSheet — scrollable bottom sheet that displays a list of users
 * with avatar, name, handle, and follow button.
 * Supports client-side pagination (loads PAGE_SIZE items, then 30 more on scroll).
 */
export function UserListSheet({ open, onOpenChange, title, pubkeys, isLoading }: UserListSheetProps) {
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Reset display count when the sheet opens
  useEffect(() => {
    if (open) {
      setDisplayCount(PAGE_SIZE);
    }
  }, [open]);

  const visiblePubkeys = pubkeys.slice(0, displayCount);
  const hasMore = displayCount < pubkeys.length;

  // Intersection observer to detect when the user scrolls to the bottom
  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore) {
        setDisplayCount((prev) => prev + PAGE_SIZE);
      }
    },
    [hasMore]
  );

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(observerCallback, { rootMargin: '100px' });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [observerCallback]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px] h-[70dvh] p-0 gap-0 rounded-2xl overflow-hidden border border-border flex flex-col">
        {/* Sticky Header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-border shrink-0 bg-background z-10">
          <DialogTitle className="font-extrabold text-[17px]">{title}</DialogTitle>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="p-1.5 rounded-full hover:bg-secondary transition-colors cursor-pointer"
          >
            <X className="size-5 text-muted-foreground" />
          </button>
        </div>

        {/* List Container */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            // Loading skeleton
            Array.from({ length: 8 }).map((_, i) => <UserRowSkeleton key={i} />)
          ) : pubkeys.length === 0 ? (
            // Empty state
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center px-6">
              <Users className="size-10 text-muted-foreground/40" />
              <p className="text-sm font-bold text-foreground">Nothing here yet</p>
              <p className="text-xs text-muted-foreground leading-normal">
                {title === 'Following'
                  ? 'Not following anyone yet. Find people to follow in the Explore tab!'
                  : 'No followers yet. Share your profile to grow your community!'}
              </p>
            </div>
          ) : (
            // User rows
            <>
              {visiblePubkeys.map((pk) => (
                <UserRow key={pk} pubkey={pk} />
              ))}

              {/* Sentinel for intersection observer */}
              <div ref={sentinelRef} className="h-4" />

              {/* Loading more indicator */}
              {hasMore && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="size-5 animate-spin text-muted-foreground" />
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default UserListSheet;
